import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Download, FileInput, GraduationCap, HardDrive, HelpCircle, Layers3, Monitor, Plus, ShieldAlert, ShieldCheck, Trash2 } from 'lucide-react'
import { CARD_PRESETS, CUSTOM_CARD_PRESET, STARTER_PROJECTS, createDeckProject, type StarterProjectId } from '../defaults'
import type { LocalStorageStatus } from '../storage'
import { formatDate } from '../utils'
import type { DeckProject } from '../types'

interface ProjectLibraryProps {
  projects: DeckProject[]
  onCreate: (project: DeckProject) => void
  onOpen: (project: DeckProject) => void
  onImport: (file: File) => void
  onDelete: (project: DeckProject) => void
  onOpenStorage: () => void
  recoveryCount: number
  damagedProjectCount: number
  storageStatus: LocalStorageStatus
  compatibilityWarningCount: number
  onOpenCompatibility: () => void
  onOpenHelp: () => void
  onStartTour: () => void
  canResumeTour: boolean
  editorSupported: boolean
  showInstall: boolean
  onOpenInstall: () => void
}

export function ProjectLibrary({
  projects,
  onCreate,
  onOpen,
  onImport,
  onDelete,
  onOpenStorage,
  recoveryCount,
  damagedProjectCount,
  storageStatus,
  compatibilityWarningCount,
  onOpenCompatibility,
  onOpenHelp,
  onStartTour,
  canResumeTour,
  editorSupported,
  showInstall,
  onOpenInstall,
}: ProjectLibraryProps) {
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('My first deck')
  const [preset, setPreset] = useState(CARD_PRESETS[0].id)
  const [includeBleed, setIncludeBleed] = useState(true)
  const [starterId, setStarterId] = useState<StarterProjectId>('cute')
  const [customWidthMm, setCustomWidthMm] = useState(CUSTOM_CARD_PRESET.widthMm)
  const [customHeightMm, setCustomHeightMm] = useState(CUSTOM_CARD_PRESET.heightMm)
  const importRef = useRef<HTMLInputElement>(null)
  const selectedPreset = CARD_PRESETS.find((item) => item.id === preset) ?? CUSTOM_CARD_PRESET
  const customSizeSelected = preset === CUSTOM_CARD_PRESET.id
  const customSizeValid = customWidthMm >= 10 && customHeightMm >= 10

  const create = () => {
    onCreate(createDeckProject(name, preset, starterId, includeBleed, customSizeSelected ? { widthMm: customWidthMm, heightMm: customHeightMm } : undefined))
    setCreating(false)
  }

  useEffect(() => {
    if (!creating) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCreating(false)
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [creating])

  useEffect(() => {
    if (!editorSupported) setCreating(false)
  }, [editorSupported])

  return (
    <main className="library-page">
      <header className="library-header">
        <div className="brand-lockup">
          <div className="brand-mark"><Layers3 size={24} /></div>
          <div>
            <div className="brand-name-row"><strong>Deckelier</strong><span className="beta-badge">Beta</span></div>
            <span>Game Card Studio</span>
          </div>
        </div>
        <div className="library-actions">
          <button className="button secondary" onClick={onOpenHelp}>
            <HelpCircle size={17} /> Help
          </button>
          <button className="button secondary storage-button" onClick={onOpenStorage}>
            <HardDrive size={17} /> Storage & recovery{recoveryCount > 0 && <span>{recoveryCount}</span>}
          </button>
          <button className="button secondary" onClick={() => importRef.current?.click()}>
            <FileInput size={17} /> Import project
          </button>
        </div>
        <input
          ref={importRef}
          hidden
          type="file"
          accept=".deckelier,application/zip"
          onChange={(event) => {
            const file = event.target.files?.[0]
            event.target.value = ''
            if (file) onImport(file)
          }}
        />
      </header>

      <section className="library-hero">
        <div className="eyebrow"><ShieldCheck size={15} /> Project data stays on your device · Nothing is sent to a server</div>
        <h1>Design the template.<br /><em>Fill the deck.</em> Print it right.</h1>
        <p>Design one reusable card template for your board game, fill the deck in a data grid, then export files for professional or home printing.</p>
        {editorSupported ? (
          <div className="library-hero-actions">
            <button className="button primary large" onClick={() => setCreating(true)}><Plus size={19} /> New deck</button>
            <button className="button secondary large" onClick={onStartTour}><GraduationCap size={19} /> {canResumeTour ? 'Resume guided tour' : 'Take the guided tour'}</button>
            {showInstall && <button className="button secondary large" onClick={onOpenInstall}><Download size={19} /> Install Deckelier</button>}
          </div>
        ) : (
          <div className="library-desktop-note">
            <Monitor size={21} />
            <span><strong>Editing requires a desktop-sized screen</strong><small>You can browse projects and Help here. Open Deckelier on a computer with a mouse or trackpad to create and edit decks.</small></span>
          </div>
        )}
      </section>

      <section className="project-section">
        {compatibilityWarningCount > 0 && (
          <button className="library-storage-warning compatibility" onClick={onOpenCompatibility}>
            <ShieldAlert size={19} />
            <span><strong>{compatibilityWarningCount} browser feature{compatibilityWarningCount === 1 ? '' : 's'} limited</strong><small>Deckelier can run, but review which optional features are unavailable.</small></span>
          </button>
        )}
        {(storageStatus.pressure === 'warning' || storageStatus.pressure === 'critical' || damagedProjectCount > 0) && (
          <button className={`library-storage-warning ${storageStatus.pressure}`} onClick={onOpenStorage}>
            <AlertTriangle size={19} />
            <span>
              <strong>{damagedProjectCount > 0 ? 'A local project needs attention' : storageStatus.pressure === 'critical' ? 'Local storage is nearly full' : 'Local storage is getting full'}</strong>
              <small>{damagedProjectCount > 0 ? 'Open Storage & recovery to review available copies.' : 'Review storage usage and export important projects.'}</small>
            </span>
          </button>
        )}
        <div className="section-heading">
          <div>
            <span className="eyebrow plain">Your workspace</span>
            <h2>Recent projects</h2>
          </div>
          <span className="muted">{projects.length} saved locally</span>
        </div>
        {projects.length === 0 && editorSupported ? (
          <button className="empty-project" onClick={() => setCreating(true)}>
            <span className="empty-project-icon"><Plus /></span>
            <strong>Create your first deck</strong>
            <span>Start from a professional card preset or a blank canvas.</span>
          </button>
        ) : projects.length === 0 ? (
          <div className="empty-project blocked">
            <span className="empty-project-icon"><Monitor /></span>
            <strong>No projects on this device</strong>
            <span>Create your first deck when you open Deckelier on a supported desktop.</span>
          </div>
        ) : (
          <div className="project-grid">
            {projects.map((project) => (
              <article className="project-card" key={project.id}>
                <button className="project-open" onClick={() => onOpen(project)}>
                  <div className="project-thumbnail" style={{ background: project.card.background }}>
                    <div className="thumbnail-card"><span>{project.name.slice(0, 1).toUpperCase()}</span></div>
                  </div>
                  <div className="project-card-copy">
                    <strong>{project.name}</strong>
                    <span>{project.rows.length} cards · {project.card.widthMm} × {project.card.heightMm} mm</span>
                    <small>Edited {formatDate(project.updatedAt)}</small>
                  </div>
                </button>
                <button className="icon-button danger project-delete" aria-label={`Delete ${project.name}`} onClick={() => onDelete(project)}>
                  <Trash2 size={16} />
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      {creating && (
        <div className="modal-backdrop" onMouseDown={() => setCreating(false)}>
          <form className="modal new-project-dialog" onSubmit={(event) => { event.preventDefault(); create() }} onMouseDown={(event) => event.stopPropagation()} aria-modal="true" aria-labelledby="new-project-title" aria-describedby="new-project-description" role="dialog">
            <div className="modal-kicker">New Deckelier project</div>
            <h2 id="new-project-title">Set up your deck</h2>
            <p className="modal-intro" id="new-project-description">Choose the finished card size first. Bleed is a separate print option.</p>
            <label className="field-label">Project name<input autoFocus value={name} onChange={(event) => setName(event.target.value)} /></label>
            <label className="field-label">Card format
              <select value={preset} onChange={(event) => {
                const nextPreset = event.target.value
                setPreset(nextPreset)
                if (nextPreset === CUSTOM_CARD_PRESET.id) setStarterId('empty')
              }}>
                {CARD_PRESETS.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.detail}</option>)}
                <option value={CUSTOM_CARD_PRESET.id}>{CUSTOM_CARD_PRESET.name} · {CUSTOM_CARD_PRESET.detail}</option>
              </select>
            </label>
            {customSizeSelected && (
              <div className="custom-size-fields">
                <label className="field-label">Trim width · mm<input type="number" min="10" step="0.01" value={customWidthMm} onChange={(event) => setCustomWidthMm(Number(event.target.value))} /></label>
                <label className="field-label">Trim height · mm<input type="number" min="10" step="0.01" value={customHeightMm} onChange={(event) => setCustomHeightMm(Number(event.target.value))} /></label>
              </div>
            )}
            <div className="setup-options">
              <label className={`setup-option ${includeBleed ? 'selected' : ''}`}>
                <input type="checkbox" checked={includeBleed} onChange={(event) => setIncludeBleed(event.target.checked)} />
                <span><strong>Add print bleed</strong><small>{selectedPreset.recommendedBleedMm} mm on every side. Recommended for commercial printing.</small></span>
              </label>
            </div>
            <fieldset className="starter-projects">
              <legend>Starter project</legend>
              <div className="starter-project-list">
                {STARTER_PROJECTS.map((starter) => {
                  const unavailable = customSizeSelected && starter.id !== 'empty'
                  return (
                    <label key={starter.id} className={`setup-option ${starterId === starter.id ? 'selected' : ''} ${unavailable ? 'disabled' : ''}`}>
                      <input
                        type="radio"
                        name="starter-project"
                        value={starter.id}
                        checked={starterId === starter.id}
                        disabled={unavailable}
                        onChange={() => setStarterId(starter.id)}
                      />
                      <span>
                        <strong>{starter.name}</strong>
                        <small>{unavailable ? 'Available for Poker, Bridge, Tarot, Mini, and Square formats.' : starter.detail}</small>
                      </span>
                    </label>
                  )
                })}
              </div>
            </fieldset>
            <div className="modal-actions">
              <button type="button" className="button ghost" onClick={() => setCreating(false)}>Cancel</button>
              <button type="submit" className="button primary" disabled={!name.trim() || (customSizeSelected && !customSizeValid)}>Create deck</button>
            </div>
          </form>
        </div>
      )}
    </main>
  )
}
